import type {
  EstimateIntercityOrderRequestBody,
  EstimateNewOrderRequestBody,
} from '@ahm/api-wrappers-core';
import { debounce } from '@ahm/common-helpers';
import { useAuth } from '@ahm/react-auth';
import { toast } from '@ahm/react-ui';
import { closeModal } from '@mantine/modals';
import { useEffect, useMemo, useRef } from 'react';
import { useEstimatedFeePartner } from '@/api/order';
import { ModalName } from '@/components/modals';
import { ROUTES } from '@/config/router';
import { useEstimateIntercityOrder } from '@/features/booking/api/intercity';

import { useEstimateOrder } from '@/features/booking/api/order/use-estimate-order';
import {
  INVALID_MAX_DISTANCE,
  INVALID_PROMOTION_CREATE,
  SERVICE_CONDITION_NOT_MET,
} from '@/lib/constants';
import { checkPartnerFromToken } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export function useEstimateBookingOrder(
  estimateRequestBody:
    | EstimateNewOrderRequestBody
    | EstimateIntercityOrderRequestBody,
  pathname
) {
  const {
    setEstimatedData,
    setEstimatedAllService,
    setErrorPromo,
    setAlertBooking,
    isEstimateAllService,
    selectedService,
    setEstimatedRequestData,
    isShowToastPromo,
    setShowToastPromo,
  } = useBookingStore((state) => state);
  const { session } = useAuth();
  const token = session?.token;

  const isPartner = token ? checkPartnerFromToken(token) : false;

  const estimatedFeePartnerHook = useEstimatedFeePartner({
    onSuccess: handleEstimateSuccess,
    onError: handleEstimateError,
  });

  const estimateOrderHook = useEstimateOrder({
    onSuccess: handleEstimateSuccess,
    onError: handleEstimateError,
  });

  const { mutate: onEstimateOrder } = isPartner
    ? estimatedFeePartnerHook
    : estimateOrderHook;

  const { mutate: onEstimateInterCityOrder } = useEstimateIntercityOrder({
    onSuccess: handleEstimateSuccess,
    onError: handleEstimateError,
  });

  const prevEstimateRequestBodyRef = useRef<string | undefined>('');

  const debounceEstimateFn = useMemo(
    () =>
      debounce((estimateRequestBody) => {
        const currentDependencies = JSON.stringify(estimateRequestBody);

        if (
          prevEstimateRequestBodyRef.current !== currentDependencies &&
          estimateRequestBody
        ) {
          setEstimatedRequestData({
            ...estimateRequestBody,
          });

          if (pathname === ROUTES.nationwide) {
            onEstimateInterCityOrder(estimateRequestBody);
          } else {
            onEstimateOrder(estimateRequestBody);
          }

          prevEstimateRequestBodyRef.current = currentDependencies;
        }
      }, 300),
    [pathname, onEstimateOrder, onEstimateInterCityOrder]
  );

  useEffect(() => {
    if (!estimateRequestBody) {
      setAlertBooking(null);
    } else {
      debounceEstimateFn(estimateRequestBody);
    }
  }, [estimateRequestBody, debounceEstimateFn]);

  function handleEstimateResponse(data) {
    const errorData = data?.error;

    if (!errorData?.code) {
      setAlertBooking(null);
    }

    if (errorData && errorData.code === SERVICE_CONDITION_NOT_MET) {
      return;
    }

    if (errorData?.code === INVALID_MAX_DISTANCE) {
      setAlertBooking({
        title: errorData.title || 'INVALID_MAX_DISTANCE',
        description: errorData.description || '',
      });
      return;
    }

    handlePromotionError(errorData, data);
    handlePromoErrorMessage(data);
  }

  function handlePromotionError(errorData, data) {
    if (errorData && errorData.code !== INVALID_PROMOTION_CREATE) {
      data.error?.title
        ? toast.error({
            title: data.error.title,
            message: data.error.description,
            withIcon: true,
          })
        : null;
      setErrorPromo(null);
    } else if (errorData && errorData.code === INVALID_PROMOTION_CREATE) {
      setErrorPromo(null);
    }
  }

  function handlePromoErrorMessage(data) {
    if (data?.data?.promo_error_message && estimateRequestBody.promo_code_ids) {
      if (isShowToastPromo) {
        toast.error({
          title: data.data.promo_error_message,
          message: null,
          withIcon: true,
          withCloseButton: false,
          onClose: () => {
            setShowToastPromo(false);
          },
        });
      }
      setErrorPromo(null);
    }
    if (
      !data?.data?.promo_error_message &&
      estimateRequestBody.promo_code_ids &&
      !data?.error
    ) {
      if (isShowToastPromo) {
        toast.success({
          title: 'Áp dụng mã khuyến mãi thành công',
          message: null,
          withIcon: true,
          withCloseButton: false,
          onClose: () => {
            setShowToastPromo(false);
          },
        });
      }
      setErrorPromo(null);
      closeModal(ModalName.Promotion);
    }
  }

  function handleEstimateSuccess(res) {
    if (!res) return;
    if (isEstimateAllService) setEstimatedAllService(res);
    const data =
      (selectedService
        ? res.find((item) => item.service_id === selectedService._id)
        : res[0]) ?? null;
    setEstimatedData(data);
    handleEstimateResponse(data);
  }

  function handleEstimateError(error) {
    const errorData = error.response?.data;

    if (errorData?.code !== INVALID_PROMOTION_CREATE) {
      errorData?.title
        ? toast.error({
            title: errorData.title,
            message: errorData.description,
            withIcon: true,
          })
        : null;
    } else if (errorData?.code === INVALID_PROMOTION_CREATE) {
      setErrorPromo(errorData?.description ?? 'error');
      closeModal(ModalName.PromotionDetail);
    }
  }

  return {
    debounceEstimateFn,
    handlePromotionError,
    handleEstimateResponse,
    handleEstimateError,
    handlePromoErrorMessage,
  };
}
